import { GlassPanel } from "@/components/ui/glass-panel";
import { Stars, Moon } from "lucide-react";
import { cn } from "@/lib/utils";
import { lunarPosition, moonPhaseFromFraction, rateNight } from "@/lib/astronomy";
import { useLocation } from "@/components/location-provider";

interface MoonPhaseWeekProps {
  daily?: { dt: number; moon_phase?: number; moonrise?: number; moonset?: number; clouds?: number }[];
}

function fmt(ts: number | undefined): string {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function dayLabel(ts: number, idx: number): string {
  if (idx === 0) return "Tonight";
  return new Date(ts * 1000).toLocaleDateString([], { weekday: "short" });
}
function dateLabel(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString([], { month: "short", day: "numeric" });
}

export function MoonPhaseWeek({ daily }: MoonPhaseWeekProps) {
  if (!daily || daily.length === 0) return null;
  const days = daily.slice(0, 7);

  return (
    <GlassPanel intensity="low" className="p-4 sm:p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-500/10 ring-1 ring-slate-500/20">
          <Moon className="h-4 w-4 text-slate-300" />
        </div>
        <div>
          <h3 className="text-sm font-bold tracking-tight">Moon · Next 7 Nights</h3>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Phase · illumination · rise/set
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {days.map((d, i) => {
          const phaseFrac = d.moon_phase ?? 0.5;
          const phase = moonPhaseFromFraction(phaseFrac);
          const illum = 1 - Math.abs(phaseFrac - 0.5) * 2;
          return (
            <div
              key={d.dt}
              className="rounded-xl border border-border/40 bg-background/30 p-3 flex flex-col items-center text-center"
            >
              <p
                className={cn(
                  "text-[10px] uppercase tracking-wider font-bold",
                  i === 0 ? "text-primary" : "text-muted-foreground",
                )}
              >
                {dayLabel(d.dt, i)}
              </p>
              <p className="text-[10px] text-muted-foreground">{dateLabel(d.dt)}</p>
              <span className="text-3xl mt-2" role="img" aria-label={phase.name}>
                {phase.emoji}
              </span>
              <p className="text-xs font-bold mt-1.5 tabular-nums">
                {Math.round(illum * 100)}%
              </p>
              <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                {phase.name}
              </p>
              <div className="mt-2 w-full grid grid-cols-2 gap-1 text-[9px]">
                <div className="rounded bg-amber-500/10 border border-amber-500/20 px-1 py-0.5">
                  <p className="text-muted-foreground">rise</p>
                  <p className="font-bold tabular-nums">{fmt(d.moonrise)}</p>
                </div>
                <div className="rounded bg-slate-500/10 border border-slate-500/20 px-1 py-0.5">
                  <p className="text-muted-foreground">set</p>
                  <p className="font-bold tabular-nums">{fmt(d.moonset)}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </GlassPanel>
  );
}

interface StargazingWeekProps {
  daily?: { dt: number; moon_phase?: number; clouds?: number }[];
}

const RATING_TONE: Record<string, { bar: string; text: string; bg: string }> = {
  Excellent: { bar: "bg-emerald-400", text: "text-emerald-400", bg: "bg-emerald-500/10" },
  Good: { bar: "bg-lime-400", text: "text-lime-400", bg: "bg-lime-500/10" },
  Fair: { bar: "bg-amber-400", text: "text-amber-400", bg: "bg-amber-500/10" },
  Poor: { bar: "bg-slate-400", text: "text-slate-400", bg: "bg-slate-500/10" },
};

export function StargazingWeek({ daily }: StargazingWeekProps) {
  const { current: location } = useLocation();
  if (!daily || daily.length === 0) return null;
  const days = daily.slice(0, 7);

  const ratings = days.map((d) => {
    const date = new Date(d.dt * 1000);
    const r = rateNight({
      date,
      cloudPct: d.clouds ?? 50,
      moonPhaseFraction: d.moon_phase ?? 0.5,
    });
    // Compute astronomical twilight rough info using lunarPosition for that night
    return r;
  });

  const best = ratings.reduce((a, b) => (b.score > a.score ? b : a));
  void location;

  return (
    <GlassPanel intensity="low" className="p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500/10 ring-1 ring-indigo-500/20">
            <Stars className="h-4 w-4 text-indigo-300" />
          </div>
          <div>
            <h3 className="text-sm font-bold tracking-tight">Stargazing · Next 7 Nights</h3>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Cloud cover + moon brightness rating
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Best night
          </p>
          <p className="text-xs font-bold">
            {best.date.toLocaleDateString([], { weekday: "short" })}{" "}
            <span className="text-muted-foreground font-normal">{best.score.toFixed(1)}/10</span>
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        {ratings.map((r, i) => {
          const tone = RATING_TONE[r.rating];
          const pct = r.score * 10;
          return (
            <div
              key={r.date.getTime()}
              className={cn(
                "rounded-lg border border-border/40 bg-background/30 px-3 py-2 flex items-center gap-3",
                r.date.getTime() === best.date.getTime() && "ring-1 ring-emerald-500/30",
              )}
            >
              <div className="flex-shrink-0 w-12 text-center">
                <p className="text-xs font-bold">
                  {i === 0 ? "Tonight" : r.date.toLocaleDateString([], { weekday: "short" })}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {r.date.toLocaleDateString([], { month: "short", day: "numeric" })}
                </p>
              </div>
              <span className="text-xl flex-shrink-0">{r.moonPhaseEmoji}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <p className={cn("text-sm font-bold", tone.text)}>{r.rating}</p>
                  <p className="text-[10px] text-muted-foreground">
                    cloud {Math.round(r.cloudPct)}% · moon {Math.round(r.moonIllumination * 100)}%
                  </p>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-border/40 overflow-hidden">
                  <div
                    className={cn("h-full rounded-full transition-all", tone.bar)}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
              <p className={cn("text-base font-bold tabular-nums w-10 text-right", tone.text)}>
                {r.score.toFixed(1)}
              </p>
            </div>
          );
        })}
      </div>
    </GlassPanel>
  );
}
